import type {
  DBRow,
  FilterEntry,
  Filters,
  NewRow,
  RecordStatus,
  Row,
} from '@cloudio-saas/datasource-types';
import { Dispatch, useStateX, useStateXValue } from '@cloudio-saas/statex';
import { useCallback } from 'react';
import { isEmpty } from 'utils/isEmpty';
import Store, { QueryRequest } from './Store';
import useDataStore from './useDataStore';
import type { Records } from './storeTypes';
import { useStoreRecordId } from 'components/DataStoreReference/DataStoreReference';
import { getName } from 'components/SmartSearch/SearchUtils';

const EMPTY_ARRAY: string[] = [];
const EMPTY_OBJECT = {};

export interface StateXStoreResult<T> {
  store: Store<T>;
  query: (
    queryRequest: QueryRequest<T>,
  ) => Promise<void | Records<T> | undefined>;
  updateRecord: (id: string, partialRecord: Partial<DBRow<T>>) => void;
  records: Records<T>;
  recordsOrder: string[];
  save: (props?: {
    silent?: boolean | undefined;
    feedback?: string | undefined;
  }) => Promise<boolean>;
  deleteRecord: (id: string) => Promise<void>;
  isAttributeDirty: (recordId: string, attribute: keyof T) => boolean;
  createNew: (props: {
    partialRecord: NewRow<T>;
    addOnTop?: boolean;
    cid?: string;
    rs: RecordStatus;
  }) => Promise<string | undefined>;
  setCurrentRecordId: Dispatch<string | undefined>;
  currentRecordId: string | undefined;
  isBusy: boolean;
  isRecordDirty: (id: string) => boolean;
  isStoreDirty: boolean;
  reset: () => void;
  resetCurrentRecord: () => void;
}

/** @internal Do not use this from controller. */
export default function useAppStore<T>(props: {
  appUid: string;
  pageId: string;
  ds: string;
  alias: string;
}): StateXStoreResult<T> {
  const store = useDataStore<T>(props);
  const [currentRecordId, setCurrentRecordId] = useStateX<string | undefined>(
    store.currentRecordIdPath,
    undefined,
  );
  const isBusy = useStateXValue(store.isBusyPath, false);
  const recordsOrder = useStateXValue<string[]>(
    store.recordsOrderPath,
    EMPTY_ARRAY,
  );
  const records = useStateXValue<Records<T>>(store.recordsPath, EMPTY_OBJECT);
  const isStoreDirty = store.isStoreDirty();

  return {
    store,
    query: store.query,
    updateRecord: store.updateRecord,
    records,
    recordsOrder,
    save: store.save,
    deleteRecord: store.deleteRecord,
    isAttributeDirty: store.isAttributeDirty,
    createNew: store.createNew,
    setCurrentRecordId,
    currentRecordId,
    isBusy,
    isRecordDirty: store.isRecordDirty,
    isStoreDirty,
    reset: store.reset,
    resetCurrentRecord: store.resetCurrentRecord,
  };
}

/**
 * Use this hook if you want to rerender for the below cases
 * 1. store busy status changes
 * 2. record's content for the given recordId changes
 * Note: this hook is usually along with useRecordsOrder hook
 * @param store The store to watch for
 * @param recordId The record id to watch for
 * @returns record - the record of the given record id
 * @returns isBusy - store's busy status. will be true if the any request to the server is pending response
 * @returns update - a callback that can be used to update the current active record
 */
function useRecord<T>(store: Store<T>, recordId: string) {
  const isBusy = useStateXValue(store.isBusyPath, false);
  const [record, setRecord] = useStateX<Row<T> | undefined>(
    [...store.recordsPath, recordId],
    undefined,
  );

  const update = useCallback(
    (partialRecord: Partial<DBRow<T>>, skipDirty = false) => {
      const r = store.processUpdateRecord(recordId, partialRecord, skipDirty);
      setRecord(r);
    },
    [recordId, setRecord, store],
  );

  return {
    record,
    isBusy,
    update,
  };
}

/**
 * Use this hook if you want to rerender for the below cases
 * 1. store busy status changes
 * 2. store's active (current) record changes
 * 3. active records content changes
 * @param store The store to watch for
 * @returns record - current active record
 * @returns currentRecordId - current active record's id
 * @returns isBusy - store's busy status. will be true if the any request to the server is pending response
 * @returns update - a callback that can be used to update the current active record
 */
function useStoreCurrentRecord<T>(store: Store<T>) {
  const currentRecordId: string | null = useStateXValue(
    store.currentRecordIdPath,
    null,
  );
  const { record, isBusy, update } = useRecord(store, currentRecordId ?? 'x');
  return {
    record,
    currentRecordId,
    isBusy,
    update,
  };
}

/**
 * Use this hook if you want to rerender whenever the store's active (current) record changes
 * @param store The store to watch for
 * @returns current active record's id
 */
function useStoreCurrentRecordId<T>(store?: Store<T> | null): string | null {
  return useStateXValue<string | null>(
    store?.currentRecordIdPath ?? ['_currentRecordIdPath_'],
    null,
  );
}

/**
 * Use this hook to invoke a callback whenever the store's active (current)
 * record changes without rerendering.
 * @param store The store to watch for
 * @param onChange a callback to be called with {value: the activated record's id, oldValue: the previous active records's id}
 */
function useStoreCurrentRecordIdCallback<T>(
  store: Store<T> | null,
  onChange: (props: { oldValue?: string | null; value: string }) => void,
) {
  useStateXValue<string | null>(
    store?.currentRecordIdPath ?? ['_currentRecordIdPath_'],
    null,
    {
      onChange: ({ value, oldValue }) => {
        if (value) {
          onChange({ value, oldValue });
        }
      },
      shouldComponentUpdate: () => false,
    },
  );
}

/**
 * Use this hook to rerender whenever the store's insertable status changes
 * @param store The store to watch for
 * @returns true if the store is insertable, false otherwise
 */
function useStoreInsertable<T>(store?: Store<T> | null): boolean {
  return useStateXValue(store?.insertablePath ?? ['x'], undefined) ?? false;
}

/**
 * Use this hook to rerender whenever the store's updatable status changes
 * @param store The store to watch for
 * @returns true if the store is updatable, false otherwise
 */
function useStoreUpdatable<T>(store?: Store<T> | null): boolean {
  return useStateXValue(store?.updatablePath ?? ['x'], undefined) ?? false;
}

/**
 * Use this hook to rerender whenever the store's deletable status changes
 * @param store The store to watch for
 * @returns true if the store is deletable, false otherwise
 */
function useStoreDeletable<T>(store?: Store<T> | null): boolean {
  return useStateXValue(store?.deletablePath ?? ['x'], undefined) ?? false;
}

/**
 * Use this hook to rerender whenever the store's busy status changes.
 * The store's busy status will be set to true during query and post operations
 * while waiting for the server to respond
 * @param store The store to watch for
 * @returns true if the store is busy, false otherwise
 */
function useStoreBusy<T>(store?: Store<T> | null): boolean {
  return useStateXValue(store?.isBusyPath ?? ['x'], undefined) ?? false;
}

/**
 * Use this hook to rerender whenever the store's sort state changes for the given attribute.
 * @param store The store to watch for
 * @param attribute The attribute to watch for
 * @returns the sort state
 */
function useStoreAttributeSortState<T>(
  store?: Store<T> | null,
  attribute = '_dummy_attr',
): number {
  if (isEmpty(attribute)) {
    attribute = '_dummy_attr';
  }
  return useStateXValue(
    store ? [...store.sortPath, attribute] : ['_dummy_sort_state_path'],
    0,
  );
}

export function useStoreAttributeFilter<T>(
  store?: Store<T> | null,
  attribute = '_dummy_attr',
): FilterEntry<T> | undefined {
  if (isEmpty(attribute)) {
    attribute = '_dummy_attr';
  }
  const filters = useStateXValue<Filters<any> | undefined>(
    store?.smartSearchFilterPath ?? ['_dummy_ss_state_path'],
    undefined,
  );
  return filters?.find((f) => getName(f) === attribute);
}

const EMPTY_FILTERS: Filters<any> = [];

export function useStoreSmartFiltersState<T>(
  store?: Store<T> | null,
): Filters<T> {
  return useStateXValue<Filters<T>>(
    store?.smartSearchFilterPath ?? ['_dummy_ss_state_path'],
    EMPTY_FILTERS,
  );
}

/**
 * Use this hook to rerender whenever the store's sort state changes.
 * @param store The store to watch for
 * @returns the sort state
 */
function useStoreSortState<T>(store?: Store<T> | null): Record<string, number> {
  return useStateXValue(
    store?.sortPath ?? ['_dummy_sort_state_path'],
    EMPTY_OBJECT,
  );
}

/**
 * Use this hook to rerender whenever the store's sort state changes.
 * @param store The store to watch for
 * @returns the sort state
 */
function useStoreGridFilterState<T>(store?: Store<T> | null): Filters<unknown> {
  const path = store?.gridFilterPath;
  return useStateXValue(path ?? ['_dummy_grid_filter_path'], EMPTY_ARRAY);
}

/**
 * Use this hook to rerender whenever the store's size (number of records) changes
 * @param store The store to watch for
 * @returns the store size
 */
function useStoreSize<T>(store?: Store<T> | null): number {
  return useStateXValue<string[]>(
    store?.recordsOrderPath ?? ['x_useStoreSize'],
    [],
  ).length;
}

/** @internal Do not use this from controller. */
function useErrorMessage(
  store: Store<any> | null,
  attribute = 'x',
  row?: number,
): string {
  const recordId = useStoreRecordId(store, row);
  return useStateXValue<string>(store?.errorPath ?? ['x_errorPath'], '', {
    params: { id: recordId, attribute },
  });
}

/**
 * Use this hook to rerender whenever the store's dirty (unsaved changes) status changes
 * @param store The store to watch for
 * @returns true if the store is dirty, false otherwise
 */
function useStoreDirty<T>(store?: Store<T> | null): boolean {
  const origRecs = useStateXValue<Records<T>>(
    store?.originalRecordsPath ?? ['x_originalRecordsPath'],
    {},
  );
  const records = useStateXValue<Records<T>>(
    store?.recordsPath ?? ['x_recordsPath'],
    {},
  );
  if (origRecs && Object.keys(origRecs).length > 0) {
    return true;
  }
  const dirtyRecords = Object.values(records).filter(
    (_record: Row<T>) => _record._rs !== 'Q' && _record._rs !== 'N',
  );
  return dirtyRecords.length > 0;
}

export {
  useErrorMessage,
  useRecord,
  useStoreAttributeSortState,
  useStoreBusy,
  useStoreCurrentRecord,
  useStoreCurrentRecordId,
  useStoreCurrentRecordIdCallback,
  useStoreDeletable,
  useStoreDirty,
  useStoreGridFilterState,
  useStoreInsertable,
  useStoreSize,
  useStoreSortState,
  useStoreUpdatable,
};
